/**
 * Dispatch Countdown Timer Web Component
 * Shows urgency-based dispatch countdown on product pages.
 *
 * Modes:
 *  - 13:45–14:00 IST → real countdown to 2 PM cutoff
 *  - Before 14:00 IST → 15-min cached timer, "dispatched today"
 *  - After 14:00 IST  → 15-min cached timer, "dispatched at the earliest"
 */

const TIMER_DURATION = 900; // 15 minutes in seconds
const CUTOFF_HOUR = 14;
const WINDOW_START_MINUTES = 825; // 13:45 in total minutes
const CUTOFF_MINUTES = 840; // 14:00 in total minutes
const STORAGE_KEY = 'kreo:dispatch-timer';

class DispatchTimer extends HTMLElement {
  connectedCallback() {
    this._timeEl = this.querySelector('.dispatch-timer__time');
    this._msgEl = this.querySelector('.dispatch-timer__message');
    if (!this._timeEl || !this._msgEl) return;

    const mode = this._getMode();
    this._remaining = this._getInitialSeconds(mode);
    this._currentMode = mode;

    this._render();
    this._interval = setInterval(() => this._tick(), 1000);
  }

  disconnectedCallback() {
    if (this._interval) clearInterval(this._interval);
  }

  _getISTTime() {
    const now = new Date();
    const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
    return new Date(utcMs + 5.5 * 3600000);
  }

  _getMode() {
    const ist = this._getISTTime();
    const totalMin = ist.getHours() * 60 + ist.getMinutes();

    if (totalMin >= WINDOW_START_MINUTES && totalMin < CUTOFF_MINUTES) {
      return 'real-countdown';
    }
    return totalMin < CUTOFF_MINUTES ? 'before-cutoff' : 'after-cutoff';
  }

  _getRealSecondsRemaining() {
    const ist = this._getISTTime();
    const target = new Date(ist);
    target.setHours(CUTOFF_HOUR, 0, 0, 0);
    return Math.max(0, Math.floor((target - ist) / 1000));
  }

  _getInitialSeconds(mode) {
    if (mode === 'real-countdown') {
      return this._getRealSecondsRemaining();
    }

    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const cache = JSON.parse(raw);
        if (cache.mode === mode) {
          const elapsed = Math.floor((Date.now() - cache.timestamp) / 1000);
          const left = cache.remaining - elapsed;
          if (left > 0) return left;
        }
      }
    } catch (e) { /* localStorage unavailable */ }

    return TIMER_DURATION;
  }

  _saveCache() {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify({
        remaining: this._remaining,
        timestamp: Date.now(),
        mode: this._currentMode
      }));
    } catch (e) { /* localStorage unavailable */ }
  }

  _tick() {
    const newMode = this._getMode();

    // Mode changed (e.g., crossed 1:45 PM or 2:00 PM boundary)
    if (newMode !== this._currentMode) {
      this._currentMode = newMode;
      this._remaining = newMode === 'real-countdown'
        ? this._getRealSecondsRemaining()
        : TIMER_DURATION;
    } else if (this._currentMode === 'real-countdown') {
      // For real countdown, always recalculate to avoid drift
      this._remaining = this._getRealSecondsRemaining();
    } else {
      this._remaining--;
    }

    // Reset cached timer when it hits zero
    if (this._remaining <= 0 && this._currentMode !== 'real-countdown') {
      this._remaining = TIMER_DURATION;
    }

    this._saveCache();
    this._render();
  }

  _render() {
    const mins = Math.floor(this._remaining / 60);
    const secs = this._remaining % 60;
    this._timeEl.textContent =
      String(mins).padStart(2, '0') + ':' + String(secs).padStart(2, '0');

    this._msgEl.textContent = this._currentMode === 'after-cutoff'
      ? 'to get it dispatched at the earliest'
      : 'to get it dispatched today';
  }
}

customElements.define('dispatch-timer', DispatchTimer);
